import { useState, useEffect, ComponentType } from 'react';
import toast from 'react-hot-toast';
import {
	Cog6ToothIcon,
	ExclamationTriangleIcon,
	WrenchScrewdriverIcon,
	PlusIcon,
	PencilIcon,
	TrashIcon,
	MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../components/layout/AppLayout';
import { Button, ConfirmDialog, Spinner } from '../components/ui';
import { ServiceModal } from '../components/providers';
import AccountTab from '../components/shared/AccountTab';
import { ApiError } from '../api';
import useDefaultServicesStore from '../stores/defaultServicesStore';
import type { Service } from '../stores/defaultServicesStore';

// Tab definitions
type TabId = 'services' | 'account';

interface Tab {
	id: TabId;
	label: string;
	icon: ComponentType<{ className?: string }>;
}

const TABS: Tab[] = [
	{ id: 'services', label: 'Services', icon: WrenchScrewdriverIcon },
	{ id: 'account', label: 'Account', icon: Cog6ToothIcon },
];

// Default Services Tab - Manage default service templates for providers
const DefaultServicesTab = () => {
	const { services, isLoading, error, fetchServices, addService, updateService, deleteService } =
		useDefaultServicesStore();
	const [searchTerm, setSearchTerm] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingService, setEditingService] = useState<Service | null>(null);
	const [deletingService, setDeletingService] = useState<Service | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	// Fetch default services on mount
	useEffect(() => {
		fetchServices();
	}, [fetchServices]);

	const filteredServices = services.filter(
		service =>
			service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			service.description.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const openAddModal = () => {
		setEditingService(null);
		setIsModalOpen(true);
	};

	const openEditModal = (service: Service) => {
		setEditingService(service);
		setIsModalOpen(true);
	};

	const handleSave = async (service: Service) => {
		try {
			if (editingService) {
				await updateService(editingService.id, service);
				toast.success('Service updated successfully!');
			} else {
				await addService(service);
				toast.success('Service added successfully!');
			}
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to save service');
			throw err;
		}
	};

	const handleDeleteFromCard = (service: Service) => {
		setDeletingService(service);
	};

	const confirmDelete = async () => {
		if (!deletingService) return;

		setIsDeleting(true);
		try {
			await deleteService(deletingService.id);
			toast.success('Service deleted successfully!');
			setDeletingService(null);
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to delete service');
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<>
			<div className="settings-card">
				<div className="settings-card__header">
					<div className="settings-card__icon">
						<WrenchScrewdriverIcon className="w-6 h-6" />
					</div>
					<div className="flex-1">
						<h2 className="settings-card__title">Default Services</h2>
						<p className="settings-card__subtitle">
							Manage default service templates for providers
						</p>
					</div>
					<Button
						variant="primary"
						size="sm"
						onClick={openAddModal}
						className="hidden sm:flex"
					>
						<PlusIcon className="w-4 h-4 mr-1" />
						Add New Service
					</Button>
				</div>

				<div className="settings-card__body">
					{/* Mobile Add Button */}
					<Button
						variant="primary"
						onClick={openAddModal}
						className="w-full mb-4 sm:hidden"
					>
						<PlusIcon className="w-4 h-4 mr-1" />
						Add New Service
					</Button>

					{/* Search Bar */}
					{!isLoading && !error && services.length > 0 && (
						<div className="service-search">
							<MagnifyingGlassIcon className="service-search__icon" />
							<input
								type="text"
								className="service-search__input"
								placeholder="Search services..."
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
							/>
						</div>
					)}

					{/* Loading State */}
					{isLoading ? (
						<div className="py-12 text-center">
							<Spinner
								size="lg"
								className="mx-auto mb-3 text-purple-600 dark:text-purple-400"
							/>
							<p className="text-sm text-gray-500 dark:text-gray-400">
								Loading services...
							</p>
						</div>
					) : error ? (
						/* Error State */
						<div className="py-12 text-center">
							<ExclamationTriangleIcon className="w-16 h-16 mx-auto text-red-300 dark:text-red-600 mb-4" />
							<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
								Failed to load services
							</h3>
							<p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
							<Button variant="secondary" onClick={() => fetchServices()}>
								Try Again
							</Button>
						</div>
					) : filteredServices.length === 0 ? (
						/* Empty State */
						<div className="py-12 text-center">
							<WrenchScrewdriverIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
							<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
								{searchTerm ? 'No services found' : 'No Default Services Yet'}
							</h3>
							<p className="text-gray-500 dark:text-gray-400 mb-4">
								{searchTerm
									? 'Try a different search term'
									: 'Get started by adding your first default service template.'}
							</p>
							{!searchTerm && (
								<Button variant="primary" onClick={openAddModal}>
									<PlusIcon className="w-4 h-4 mr-1" />
									Add First Service
								</Button>
							)}
						</div>
					) : (
						/* Services Grid */
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
							{filteredServices.map(service => (
								<div key={service.id} className="service-card">
									<div className="service-card__body">
										<h3 className="service-card__name">{service.name}</h3>
										{service.description && (
											<p className="service-card__description">
												{service.description}
											</p>
										)}
										<div className="service-card__meta">
											{service.variants.length} combination
											{service.variants.length !== 1 ? 's' : ''}
										</div>
									</div>
									<div className="service-card__footer">
										<button
											onClick={() => openEditModal(service)}
											className="service-card__action service-card__action--edit"
											title="Edit service"
										>
											<PencilIcon className="w-4 h-4" />
											<span>Edit</span>
										</button>
										<button
											onClick={() => handleDeleteFromCard(service)}
											className="service-card__action service-card__action--delete"
											title="Delete service"
										>
											<TrashIcon className="w-4 h-4" />
										</button>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			<ServiceModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				service={editingService}
				onSave={handleSave}
				onDelete={(id: string) => {
					setIsModalOpen(false);
					const svc = services.find(s => s.id === id);
					if (svc) setDeletingService(svc);
				}}
			/>

			<ConfirmDialog
				isOpen={!!deletingService}
				onClose={() => setDeletingService(null)}
				onConfirm={confirmDelete}
				title="Delete Service"
				message={`Are you sure you want to delete "${deletingService?.name}" and all its pricing combinations? This action cannot be undone.`}
				confirmLabel="Yes, Delete"
				variant="danger"
				isLoading={isDeleting}
			/>
		</>
	);
};

const AdminSettings = () => {
	const [activeTab, setActiveTab] = useState<TabId>('services');

	const renderTabContent = () => {
		switch (activeTab) {
			case 'services':
				return <DefaultServicesTab />;
			case 'account':
				return <AccountTab />;
			default:
				return null;
		}
	};

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
					Settings
				</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					Manage default services and account settings
				</p>
			</div>

			{/* Settings Tabs */}
			<div className="settings-tabs">
				{TABS.map(tab => (
					<button
						key={tab.id}
						className={`settings-tab ${activeTab === tab.id ? 'settings-tab--active' : ''}`}
						onClick={() => setActiveTab(tab.id)}
					>
						<tab.icon className="w-4 h-4 md:hidden" />
						<span className="hidden md:inline">{tab.label}</span>
						<span className="md:hidden">{tab.label.split(' ')[0]}</span>
					</button>
				))}
			</div>

			{/* Tab Content */}
			<div className="settings-content">{renderTabContent()}</div>
		</AppLayout>
	);
};

export default AdminSettings;
